import { Coins, Download, Share, SquarePlus, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { BonusActionType } from 'shared/enums/bonus-action/bonus-action-type.enum';
import { bonusActionApi } from 'src/api/bonus-action/bonus-action';
import { useUserAccountDetailsContext } from 'src/contexts/UserAccountDetailsContext/UserAccountDetailsContext';
import { useUserSettingsContext } from 'src/contexts/UserSettingsContext/UserSettingsContext';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import { useUserCurrency } from 'src/hooks/use-user-currency';
import {
  clearDeferredPwaInstallPrompt,
  getDeferredPwaInstallPrompt,
  subscribeToPwaInstallPrompt,
} from 'src/pwa/install-prompt';
import {
  hasMeaningfulEarn,
  isIosSafari,
  isPwaStandalone,
  notifyPwaInstallAccepted,
  PWA_INSTALL_ACCEPTED_EVENT,
  PWA_MEANINGFUL_EARN_EVENT,
} from 'src/pwa/pwa';
import { pushRepeatableDataLayerEvent } from 'src/utils/gtm';
import { GtmEventName } from 'src/utils/gtm-event-name.enum';
import { removeLanguageFromPath } from 'src/utils/languages';
import {
  canUseBrowserNotifications,
  triggerContextualNotificationPrompt,
} from 'src/utils/notifications/browser-notifications';

const LAST_VISIT_KEY = 'rewardbloom.pwa.last-visit-at';
const VISIT_COUNTED_KEY = 'rewardbloom.pwa.visit-counted';
const RETURN_VISIT_KEY = 'rewardbloom.pwa.return-visit';
const DISMISSED_AT_KEY = 'rewardbloom.pwa.install-dismissed-at';
const DISMISSAL_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

const isInstallRoute = (pathname: string): boolean => {
  const pathWithoutLanguage = removeLanguageFromPath(pathname);

  return ['/home', '/games', '/my-rewards', '/bonus-points', '/affiliate', '/my-account'].some(
    (route) => pathWithoutLanguage === route || pathWithoutLanguage.startsWith(`${route}/`)
  );
};

const hasActiveDismissal = (): boolean => {
  try {
    const dismissedAt = Number(window.localStorage.getItem(DISMISSED_AT_KEY));
    return Number.isFinite(dismissedAt) && dismissedAt > 0 && Date.now() - dismissedAt < DISMISSAL_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const recordReturnVisit = (): boolean => {
  try {
    const recordedEligibility = window.sessionStorage.getItem(RETURN_VISIT_KEY);
    if (recordedEligibility !== null) {
      return recordedEligibility === 'true';
    }

    const hasPriorVisit = window.localStorage.getItem(LAST_VISIT_KEY) !== null;
    window.sessionStorage.setItem(RETURN_VISIT_KEY, hasPriorVisit.toString());

    if (window.sessionStorage.getItem(VISIT_COUNTED_KEY) !== 'true') {
      window.sessionStorage.setItem(VISIT_COUNTED_KEY, 'true');
      window.localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
    }

    return hasPriorVisit;
  } catch {
    return false;
  }
};

const PwaInstallPrompt: React.FC = () => {
  const t = useTranslationByKey('COMPONENTS.PWA_INSTALL');
  const { formatPoints } = useUserCurrency();
  const location = useLocation();
  const { userSettings, userSettingsLoaded } = useUserSettingsContext();
  const { refreshAccountDetails } = useUserAccountDetailsContext();
  const [deferredPrompt, setDeferredPrompt] = useState(getDeferredPwaInstallPrompt);
  const [isInstalled, setIsInstalled] = useState(isPwaStandalone);
  const [isEngagementEligible, setIsEngagementEligible] = useState(hasMeaningfulEarn);
  const [isDismissed, setIsDismissed] = useState(hasActiveDismissal);
  const [showIosGuidance, setShowIosGuidance] = useState(false);
  const eligibilityTrackedRef = useRef(false);
  const displayTrackedRef = useRef(false);
  const rewardClaimStartedRef = useRef(false);

  const claimPwaInstallReward = useCallback(async () => {
    if (rewardClaimStartedRef.current) {
      return;
    }

    rewardClaimStartedRef.current = true;

    try {
      const actions = await bonusActionApi.getAvailableBonusActions();
      const installAction = actions.find((action) => action.type === BonusActionType.PwaInstall);

      if (!installAction) {
        rewardClaimStartedRef.current = false;
        return;
      }

      if (installAction.completed) {
        return;
      }

      const completedAction = await bonusActionApi.completeBonusAction(BonusActionType.PwaInstall);
      await refreshAccountDetails();
      toast.success(
        t('REWARD_EARNED', {
          defaultValue: 'RewardBloom installed — {{points}} added!',
          points: formatPoints(completedAction.pointsAmount),
        })
      );
    } catch (error) {
      rewardClaimStartedRef.current = false;
      console.error('Failed to claim the PWA install reward:', error);
    }
  }, [formatPoints, refreshAccountDetails, t]);

  useEffect(() => {
    setIsEngagementEligible((currentValue) => currentValue || recordReturnVisit());
  }, []);

  useEffect(() => {
    const handleMeaningfulEarn = () => setIsEngagementEligible(true);
    const unsubscribe = subscribeToPwaInstallPrompt(setDeferredPrompt);
    const markInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      clearDeferredPwaInstallPrompt();
      try {
        window.localStorage.removeItem(DISMISSED_AT_KEY);
      } catch {
        // Installation state is also available through display-mode detection.
      }
      void claimPwaInstallReward();
    };
    const handleAppInstalled = () => {
      markInstalled();
      pushRepeatableDataLayerEvent(GtmEventName.PwaInstalled);
    };
    const handleInstallAccepted = () => markInstalled();

    window.addEventListener(PWA_MEANINGFUL_EARN_EVENT, handleMeaningfulEarn);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener(PWA_INSTALL_ACCEPTED_EVENT, handleInstallAccepted);

    return () => {
      unsubscribe();
      window.removeEventListener(PWA_MEANINGFUL_EARN_EVENT, handleMeaningfulEarn);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener(PWA_INSTALL_ACCEPTED_EVENT, handleInstallAccepted);
    };
  }, [claimPwaInstallReward]);

  useEffect(() => {
    if (isInstalled) {
      void claimPwaInstallReward();
    }
  }, [claimPwaInstallReward, isInstalled]);

  useEffect(() => {
    if (
      !isInstalled ||
      !userSettingsLoaded ||
      !canUseBrowserNotifications() ||
      userSettings?.browserNotificationsEnabled === true ||
      userSettings?.browserNotificationsPromptDismissed === true
    ) {
      return;
    }

    triggerContextualNotificationPrompt('pwa-install');
  }, [
    isInstalled,
    userSettings?.browserNotificationsEnabled,
    userSettings?.browserNotificationsPromptDismissed,
    userSettingsLoaded,
  ]);

  useEffect(() => {
    const platform = deferredPrompt ? 'beforeinstallprompt' : isIosSafari() ? 'ios_safari' : null;

    if (!platform || eligibilityTrackedRef.current) {
      return;
    }

    eligibilityTrackedRef.current = true;
    pushRepeatableDataLayerEvent(GtmEventName.PwaInstallPromptEligible, {
      platform,
    });
  }, [deferredPrompt]);

  const onboardingComplete =
    userSettingsLoaded &&
    userSettings?.postRegistrationFormCompletion === true &&
    userSettings?.profileSurveyCompletion === true;

  const pathWithoutLanguage = removeLanguageFromPath(location.pathname);
  const hasPendingNotificationPrompt =
    pathWithoutLanguage === '/home' &&
    canUseBrowserNotifications() &&
    userSettings?.browserNotificationsEnabled !== true &&
    userSettings?.browserNotificationsPromptDismissed !== true;

  const canOfferInstall = deferredPrompt !== null || isIosSafari();
  const shouldShow =
    !isInstalled &&
    !isDismissed &&
    !hasPendingNotificationPrompt &&
    isEngagementEligible &&
    onboardingComplete &&
    isInstallRoute(location.pathname) &&
    canOfferInstall;

  useEffect(() => {
    if (!shouldShow || displayTrackedRef.current) {
      return;
    }

    displayTrackedRef.current = true;
    pushRepeatableDataLayerEvent(GtmEventName.PwaInstallCtaDisplayed, {
      platform: deferredPrompt ? 'beforeinstallprompt' : 'ios_safari',
      eligibility_reason: hasMeaningfulEarn() ? 'meaningful_earn' : 'repeat_visit',
    });
  }, [deferredPrompt, shouldShow]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    setShowIosGuidance(false);
    try {
      window.localStorage.setItem(DISMISSED_AT_KEY, Date.now().toString());
    } catch {
      // State still remains dismissed for the current page.
    }
    pushRepeatableDataLayerEvent(GtmEventName.PwaInstallDismissed, {
      stage: showIosGuidance ? 'ios_guidance' : 'cta',
    });
  }, [showIosGuidance]);

  const install = useCallback(async () => {
    if (!deferredPrompt) {
      setShowIosGuidance(true);
      pushRepeatableDataLayerEvent(GtmEventName.PwaIosGuidanceDisplayed);
      return;
    }

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    clearDeferredPwaInstallPrompt();

    pushRepeatableDataLayerEvent(
      choice.outcome === 'accepted' ? GtmEventName.PwaInstallAccepted : GtmEventName.PwaInstallDismissed,
      {
        platform: choice.platform || 'beforeinstallprompt',
        stage: 'native_prompt',
      }
    );

    if (choice.outcome === 'accepted') {
      notifyPwaInstallAccepted();
    } else {
      setIsDismissed(true);
      try {
        window.localStorage.setItem(DISMISSED_AT_KEY, Date.now().toString());
      } catch {
        // State still remains dismissed for the current page.
      }
    }
  }, [deferredPrompt]);

  const copy = useMemo(
    () => ({
      title: t('TITLE', { defaultValue: 'Keep RewardBloom close' }),
      subtitle: t('SUBTITLE', {
        defaultValue: 'Add RewardBloom to your home screen and jump back into your progress.',
      }),
      install: t('INSTALL', { defaultValue: 'Install app' }),
      howToInstall: t('HOW_TO_INSTALL', { defaultValue: 'How to install' }),
      dismiss: t('DISMISS', { defaultValue: 'Not now' }),
      reward: t('REWARD', { defaultValue: '+{{points}}', points: formatPoints(20) }),
      iosTitle: t('IOS_TITLE', { defaultValue: 'Add RewardBloom to your Home Screen' }),
      iosStepOne: t('IOS_STEP_ONE', { defaultValue: 'Tap the Share button in Safari.' }),
      iosStepTwo: t('IOS_STEP_TWO', { defaultValue: 'Choose “Add to Home Screen”.' }),
    }),
    [formatPoints, t]
  );

  if (!shouldShow) {
    return null;
  }

  return (
    <aside
      className="fixed bottom-24 left-4 right-4 z-[115] md:bottom-6 md:left-[20rem] md:right-6"
      aria-label={copy.title}
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-violet-200 bg-white p-4 text-left shadow-2xl shadow-violet-950/15">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
            <Download className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-spaceGrotesk text-base font-bold text-gray-900">
                {showIosGuidance ? copy.iosTitle : copy.title}
              </p>
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-[11px] font-extrabold text-amber-700 ring-1 ring-amber-200">
                <Coins className="h-3.5 w-3.5" aria-hidden="true" />
                {copy.reward}
              </span>
            </div>
            {showIosGuidance ? (
              <ol className="mt-2 space-y-2 text-sm font-medium text-gray-600">
                <li className="flex items-center gap-2">
                  <Share className="h-4 w-4 shrink-0 text-violet-700" aria-hidden="true" />
                  <span>{copy.iosStepOne}</span>
                </li>
                <li className="flex items-center gap-2">
                  <SquarePlus className="h-4 w-4 shrink-0 text-violet-700" aria-hidden="true" />
                  <span>{copy.iosStepTwo}</span>
                </li>
              </ol>
            ) : (
              <p className="mt-1 text-sm font-medium text-gray-600">{copy.subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-violet-400"
            aria-label={copy.dismiss}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {!showIosGuidance && (
          <div className="mt-4 flex gap-2 md:justify-end">
            <button
              type="button"
              onClick={dismiss}
              className="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-400 md:flex-none"
            >
              {copy.dismiss}
            </button>
            <button
              type="button"
              onClick={() => void install()}
              className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-violet-700 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 md:flex-none"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              {deferredPrompt ? copy.install : copy.howToInstall}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default PwaInstallPrompt;
